import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import Button from '@shared/atoms/Button';
import HorizontalRule from '@shared/atoms/HorizontalRule';
import FormAutocompleteWithList from '@shared/atoms/FormFields/FormAutocompleteWithList';
import FormInput from '@shared/atoms/FormFields/FormInput';
import FormSelect from '@shared/atoms/FormFields/FormSelect';
import Snackbar from '@shared/atoms/Snackbar';
import T from '@shared/atoms/Typography';
import lang from '@shared/config/lang';
import { AUTOCOMPLETEWITHLIST_TYPE } from '@shared/const';
import theme from '@shared/themes/default';
import { newSelectTranslation, useForm } from '@shared/utils';

import { useAppGlobals } from '../../contexts/AppContext';

import { updateDomain, updateFeaturedCourses } from './graphql/mutations';
import { getDomain, listCourses, listFeaturedCourses } from './graphql/queries';

const OuterWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.m1};
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${theme.spacing.l};
`;

const InnerWrapper = styled.div`
  flex-basis: 50%;
  flex-grow: 0;
`;

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.m1};
`;

const DomainsView = () => {
  const [hasError, setHasError] = useState(false);
  const [hasSuccess, setHasSuccess] = useState(false);
  const [selectedDomainLanguages, setSelectedDomainLanguages] = useState();

  const {
    domain,
    useGraphQLMutation,
    useNewGraphQLQuery,
    useLazyGraphQLQuery,
  } = useAppGlobals();
  const { t } = useTranslation();

  const [listCoursesQuery, listCoursesResponse] =
    useLazyGraphQLQuery(listCourses);
  const limit = 2000;

  const getDomainResponse = useNewGraphQLQuery(getDomain, {
    variables: { domain },
  });
  const [listFeaturedCoursesQuery, listFeaturedCoursesResponse] =
    useLazyGraphQLQuery(listFeaturedCourses);
  const [updateFeaturedCoursesMutation, updateFeaturedCoursesResponse] =
    useGraphQLMutation(updateFeaturedCourses, {
      update(cache, { data: { updateFeaturedCourses } }) {
        cache.writeQuery({
          query: listFeaturedCourses,
          data: { listFeaturedCourses: updateFeaturedCourses },
        });
      },
    });
  const [updateDomainMutation, updateDomainResponse] = useGraphQLMutation(
    updateDomain,
    {
      onCompleted() {
        setHasSuccess(true);
      },
      onError() {
        setHasError(true);
      },
    }
  );

  const selectedDomain = getDomainResponse.data?.getDomain;
  const featuredCourses =
    (!listFeaturedCoursesResponse.loading &&
      listFeaturedCoursesResponse.data?.listFeaturedCourses) ||
    [];

  const {
    disableSubmit,
    errors: formErrors,
    handleBlur,
    handleChange,
    handleSubmit,
    properties,
    submitting,
    setDefaults,
    values,
  } = useForm({
    fields: {
      name: {
        required: true,
      },
      currency: {
        required: true,
      },
      defaultLanguageCode: {
        required: true,
      },
    },
    onSubmitHook: (domain) => {
      const updateLanguages = selectedDomainLanguages.map(({ key }) => key);
      const deleteLanguages = selectedDomain.languages
        .map((language) => language.code)
        .filter((code) => !updateLanguages.includes(code));

      const languages = {
        updateLanguages,
      };

      if (deleteLanguages.length > 0) {
        languages.deleteLanguages = deleteLanguages;
      }

      updateDomainMutation({
        variables: {
          input: {
            ...domain,
            id: selectedDomain.id,
            languages,
          },
        },
      });
    },
    onSubmitHookEnded: !updateDomainResponse.loading,
  });

  useEffect(() => {
    const { data, loading } = getDomainResponse;

    if (!loading && data) {
      listCoursesQuery({
        variables: {
          pagination: {
            offset: 0,
            limit,
          },
          filter: {
            status: 'PUBLISHED',
          },
          organizationId: 'global',
        },
      });
      listFeaturedCoursesQuery();
      setSelectedDomainLanguages(
        lang.data.languagesSuggestions
          .map(({ id, name }) => ({ key: id, title: name }))
          .filter(({ key }) =>
            data.getDomain.languages
              .map((language) => language.code)
              .includes(key)
          )
      );
      setDefaults({
        name: data.getDomain.name,
        currency: data.getDomain.currency,
        defaultLanguageCode: data.getDomain.defaultLanguage.code,
      });
    }
  }, [getDomainResponse]);

  useEffect(() => {
    const { data, loading } = listCoursesResponse;

    if (!loading && data) {
      if (
        data.listCourses.length > 0 &&
        data.listCourses.length % limit === 0
      ) {
        listCoursesResponse.fetchMore({
          variables: {
            pagination: {
              limit,
              offset: data.listCourses.length,
            },
          },
        });
      }
    }
  }, [
    listCoursesResponse.data?.listCourses?.length,
    listCoursesResponse.loading,
  ]);

  return (
    <>
      <OuterWrapper>
        <T fontWeight="medium" variant="headingS">
          {t('backoffice.domains.title')}
        </T>
        <HorizontalRule />
        <ContentWrapper>
          <InnerWrapper>
            {selectedDomain && (
              <InputWrapper>
                <T fontWeight="bold" variant="subtitle">
                  {t('backoffice.domains.editDomain')}
                </T>
                <FormInput
                  id="domain"
                  name="domain"
                  disabled
                  label={t('backoffice.domains.domain')}
                  required
                  value={selectedDomain.domain}
                />
                <FormInput
                  id="name"
                  name="name"
                  label={t('backoffice.domains.name')}
                  errors={formErrors}
                  handleBlur={handleBlur}
                  handleChange={handleChange}
                  properties={properties}
                  values={values}
                />
                <FormSelect
                  id="currency"
                  name="currency"
                  items={lang.data.currencySuggestions.map((currency) => ({
                    value: currency,
                    key: currency,
                    label: currency,
                  }))}
                  label={t('backoffice.domains.currency')}
                  handleChange={handleChange}
                  properties={properties}
                  values={values}
                />
                <FormAutocompleteWithList
                  id="languages"
                  name="languages"
                  disabled={submitting}
                  items={lang.data.languagesSuggestions.map(({ id, name }) => ({
                    key: id,
                    title: name,
                  }))}
                  label={t('backoffice.domains.languages')}
                  loadingText={t('generic.loadingText')}
                  noOptionsText={t('generic.noSearchResults')}
                  onChange={(languages) => {
                    setSelectedDomainLanguages(languages);
                  }}
                  required
                  selectedItems={selectedDomainLanguages}
                  theme="dark"
                  type={AUTOCOMPLETEWITHLIST_TYPE.FORM}
                />
                <FormSelect
                  id="defaultLanguageCode"
                  name="defaultLanguageCode"
                  items={lang.data.languagesSuggestions.map(({ id, name }) => ({
                    value: id,
                    key: id,
                    label: name,
                  }))}
                  label={t('backoffice.domains.defaultLanguage')}
                  handleChange={handleChange}
                  properties={properties}
                  values={values}
                />
                <Button
                  disabled={disableSubmit}
                  loading={submitting}
                  onClick={handleSubmit}
                >
                  {t('backoffice.domains.save')}
                </Button>
                <T fontWeight="bold" variant="subtitle">
                  {t('backoffice.domains.editPromotedCourse')}
                </T>
                <FormAutocompleteWithList
                  id="promoted-course"
                  name="promoted-course"
                  disabled={updateFeaturedCoursesResponse.loading}
                  items={(listCoursesResponse.data?.listCourses || [])
                    .map(({ id, name }) => ({
                      key: id,
                      title: newSelectTranslation(
                        name,
                        selectedDomain.defaultLanguage.code,
                        `<No name, ID: ${id}>`
                      ),
                    }))
                    .filter((course) => !course.archivedScheduledAt)}
                  label={t('backoffice.domains.promotedCourse')}
                  loading={
                    listCoursesResponse.loading &&
                    listFeaturedCoursesResponse.loading
                  }
                  loadingText={t('generic.loadingText')}
                  noOptionsText={t('generic.noSearchResults')}
                  onChange={(courseIds) => {
                    updateFeaturedCoursesMutation({
                      variables: {
                        input: {
                          courseIds: courseIds.map(({ key }) => key),
                        },
                      },
                    });
                  }}
                  required
                  selectedItems={featuredCourses.map(({ id, name }) => ({
                    key: id,
                    title: newSelectTranslation(
                      name,
                      selectedDomain.defaultLanguage.code,
                      `<No name, ID: ${id}>`
                    ),
                  }))}
                  theme="dark"
                  type={AUTOCOMPLETEWITHLIST_TYPE.FORM}
                />
              </InputWrapper>
            )}
          </InnerWrapper>
        </ContentWrapper>
      </OuterWrapper>
      <Snackbar
        type="success"
        message={t('backoffice.domains.successfullySaved')}
        open={hasSuccess}
        onClose={() => setHasSuccess(false)}
      />
      <Snackbar
        type="error"
        message={t('backoffice.domains.failedToSave')}
        open={hasError}
        onClose={() => setHasError(false)}
      />
    </>
  );
};

export default DomainsView;
